import React, { useState, useEffect } from "react";
import { Text, Toggle, Link, Button } from "@merge-api/merge-javascript-shared";
import { MergeLinkCustomizationStatus, WhiteLabelGuideType } from "../context/CustomizationContext";
import ConfigurationSettingsCard from "../helpers/ConfigurationSettingsCard";
import { CONFIGURATION_LINK_CONFIGURATION_PATH } from "../../../../../router/RouterUtils";
import useAppContext from "../../../../context/useAppContext";
import NoPermissionsBlock from "../../../../common/NoPermissionsBlock";
import BrandingWhiteLabelGuideModal from "../modals/BrandingWhiteLabelGuideModal";
import whiteLabelPNG from "../../../../../assets/img/white-label-guides-feature.png";
import useCustomizationContext from "../context/useCustomizationContext";

const ConfigurationBrandingWhiteLabelGuideCard = () => {
  // STATE
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [whiteLabelGuide, setWhiteLabelGuide] = useState<WhiteLabelGuideType>({
    subdomain: "",
    square_logo: null,
    active: false,
  });
  const [hasSavedWhiteLabelGuide, setHasSavedWhiteLabelGuide] = useState<boolean>(false);

  // HOOKS
  const { isUserPrivileged } = useAppContext();
  const { organizationCustomization, setOrganizationCustomization } = useCustomizationContext();
  const [showWhiteLabelGuide, setShowWhiteLabelGuide] = useState<boolean>(
    organizationCustomization?.white_label_guide_configuration?.active ?? false,
  );

  useEffect(() => {
    setWhiteLabelGuide(
      organizationCustomization?.white_label_guide_configuration || {
        subdomain: "",
        square_logo: null,
        active: false,
      },
    );
    setHasSavedWhiteLabelGuide(!!organizationCustomization?.white_label_guide_configuration);
    setShowWhiteLabelGuide(
      organizationCustomization?.white_label_guide_configuration?.active ?? false,
    );
  }, [organizationCustomization]);

  // Handlers for creating white label guide
  const handleWhiteLabelGuideChanges = (whiteLabelGuide: WhiteLabelGuideType) => {
    setWhiteLabelGuide(whiteLabelGuide);
  };
  const handleHasSavedWhiteLabelGuide = (isWhiteLabelGuideSaved: boolean) => {
    setHasSavedWhiteLabelGuide(isWhiteLabelGuideSaved);
  };

  // Handler for toggling show_white_label_guide
  const updateShowWhiteLabelGuide = (newValue: boolean) => {
    setShowWhiteLabelGuide(newValue);
    // either create new org customization value or update existing one
    if (organizationCustomization != undefined) {
      const newOrgCustomization = { ...organizationCustomization };
      newOrgCustomization.white_label_guide_configuration!.active = newValue;
      setOrganizationCustomization(newOrgCustomization);
    } else {
      // there really shouldn't be a case where this is null
      const currentOrganizationCustomization = {
        status: "PUBLISHED" as MergeLinkCustomizationStatus,
        show_white_label_guide: newValue,
      };
      setOrganizationCustomization(currentOrganizationCustomization);
    }
  };

  return isUserPrivileged ? (
    <ConfigurationSettingsCard
      title="Enable white-label guides"
      rightHandContent={
        hasSavedWhiteLabelGuide ? (
          <></>
        ) : (
          <>
            <Button
              size="sm"
              disabled={hasSavedWhiteLabelGuide}
              onClick={() => setIsModalOpen(true)}
            >
              Set up
            </Button>
            <BrandingWhiteLabelGuideModal
              whiteLabelGuide={whiteLabelGuide}
              handleWhiteLabelGuideChanges={handleWhiteLabelGuideChanges}
              handleHasSavedWhiteLabelGuide={handleHasSavedWhiteLabelGuide}
              isModalOpen={isModalOpen}
              onHide={() => setIsModalOpen(false)}
            />
          </>
        )
      }
    >
      {hasSavedWhiteLabelGuide ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-row justify-between items-start">
            <Text className="max-w-xl">
              Automatically point URLs shown to your user during authentication in Merge Link to a
              white-labeled version with your logo and preferred subdomain. Any customized links
              saved in <Link href={CONFIGURATION_LINK_CONFIGURATION_PATH}>Configuration</Link> will
              still be used.
            </Text>
            <Toggle
              label={showWhiteLabelGuide ? "Enabled" : "Disabled"}
              checked={showWhiteLabelGuide}
              onChange={() => {
                updateShowWhiteLabelGuide(!showWhiteLabelGuide);
              }}
            />
          </div>
          <div className="flex flex-row justify-between items-center">
            <div className="font-semibold">Subdomain</div>
            <Text className="bg-gray-10 text-gray-40 rounded-md h-10 items-center flex pl-4 pr-4">
              https://<span className="text-black">{whiteLabelGuide.subdomain}</span>
              .integrations.guide
            </Text>
          </div>
        </div>
      ) : (
        <img className="max-w-full" src={whiteLabelPNG} />
      )}
    </ConfigurationSettingsCard>
  ) : (
    <NoPermissionsBlock restrictedPageName="White-label guides" />
  );
};

export default ConfigurationBrandingWhiteLabelGuideCard;
